import { makeRequest } from '@/base/MaRequestPooler.js'

/**
 * Adds a new competitor group.
 *
 * @param {object} competitorGroup - The competitor group data to be added.
 * @param {string} competitorGroup.groupName - The name of the competitor group.
 * @param {string} competitorGroup.trackId - The ID of the track associated with the group.
 * @param {string} competitorGroup.colorCode - The color code representing the group.
 * @returns {Promise<object>} The response object containing success status and data.
 */
export function addCompetitorGroup(competitorGroup) {
  return makeRequest(`/account/competitor-group`, {
    method: 'POST',
    data: competitorGroup,
  })
}

/**
 * Removes a competitor group by ID.
 *
 * @param {number|string} groupId - The ID of the competitor group to be removed.
 * @returns {Promise<object>} The response object containing success status.
 */
export function removeCompetitorGroup(groupId) {
  return makeRequest(`/account/competitor-group/${groupId}`, {
    method: 'DELETE',
  })
}

/**
 * Edits an existing competitor group.
 *
 * @param {object} competitorGroup - The competitor group data to be updated.
 * @param {number|string} competitorGroup.id - The ID of the competitor group.
 * @param {string} competitorGroup.groupName - The updated name of the competitor group.
 * @param {string} competitorGroup.colorCode - The updated color code representing the group.
 * @returns {Promise<object>} The response object containing success status.
 */
export function editCompetitorGroup(competitorGroup) {
  return makeRequest(`/account/competitor-group`, {
    method: 'PUT',
    data: competitorGroup,
  })
}

/**
 * Retrieves competitor groups associated with a specific track ID.
 *
 * @param {number|string} trackId - The ID of the track for which competitor groups are being retrieved.
 * @returns {Promise<object>} The response object containing success status and a list of competitor groups.
 */
export function getCompetitorGroupsForTrackId(trackId) {
  return makeRequest(`/account/competitor-group/track-id/${trackId}`, {
    method: 'GET',
  })
}

/**
 * Adds an app to a competitor group.
 *
 * @param {number|string} groupId - The ID of the competitor group.
 * @param {number|string} appId - The ID of the app to be added to the group.
 * @returns {Promise<object>} The response object containing success status.
 */
export function saveAppInCompetitorGroup(groupId, appId) {
  return makeRequest(`/account/competitor-group/app`, {
    method: 'POST',
    data: {
      groupId,
      appId,
    },
  })
}

/**
 * Deletes an app from a competitor group or all competitor groups.
 *
 * @param {number|string} groupId - The ID of the competitor group from which the app will be removed.
 * @param {number|string} appId - The ID of the app to be removed.
 * @param {boolean} [removeFromAllGroups] - Whether to remove the app from all competitor groups. Defaults to false.
 * @returns {Promise<object>} The response object containing success status.
 */
export function deleteAppInCompetitorGroup(groupId, appId, removeFromAllGroups = false) {
  return makeRequest(`/account/competitor-group/app`, {
    method: 'DELETE',
    data: {
      groupId,
      appId,
      removeFromAllGroups,
    },
  })
}
